<template>
  <wui-modal
    :size="40"
    :closable="false"
    class="!w-[433px] phone:!w-[360px] modal-scroll"
  >
    <template #header>
      <div class="flex justify-between items-center w-full">
        <h3
          class="text-heading-sb-18-24 text-fg-base-fg3"
          data-test="modal-title"
        >
          {{ addOrEditContactText }}
        </h3>
        <wui-btn
          :title="$t('common.closeWindow')"
          icon
          prepend-icon="closeRegularMdM"
          variant="neut"
          state="alpha"
          @click="closeModal(false)"
        />
      </div>
    </template>

    <div class="flex flex-col gap-3">
      <user-avatar-input
        v-model="avatar"
        class="mx-auto mt-3 mb-9"
        :old-avatar-url="contact?.id && contact?.isPhotoAvailable ? getUserAvatarUrl(contact?.id) : ''"
        @remove-old-avatar="isOldAvatarRemoved = true"
      />

      <wui-input
        v-model.trim="form.name"
        :placeholder="`${t('mainAside.contactFio')}*`"
        :error="!nameIsValid"
        maxlength="255"
      />
      <wui-input
        v-model.trim="form.mobilePhone"
        type="tel"
        maxlength="15"
        :placeholder="`${t('common.phoneNumber')}*`"
        :error="!mobilePhoneIsValid || isDuplicatedMobilePhone"
        @input="replaceNotNumbers"
      />
      <p
        v-if="isDuplicatedMobilePhone"
        class="text-1112 text-warning-shades-900 ml-4"
      >
        {{ t('mainAside.externalContactAlreadyExists', { name: duplicatedContactMobilePhoneName }) }}
      </p>
      <div class="h-px my-1 mx-1 rounded-8 bg-bor-divider" />
      <wui-input
        v-model.trim="form.email"
        type="email"
        maxlength="46"
        :placeholder="t('common.email')"
        :error="!emailIsValid"
        :supporting-text="!emailIsValid ? t('common.emailIsNotCorrect') : ''"
        @input="emailIsValid = true"
        @blur="validateEmail"
      />

      <wui-input
        v-model.trim="form.organization"
        maxlength="255"
        :placeholder="t('common.organization')"
      />
      <wui-input
        v-model.trim="form.organizationalUnit"
        maxlength="255"
        :placeholder="t('common.department')"
      />
      <wui-input
        v-model.trim="form.position"
        maxlength="255"
        :placeholder="t('common.position')"
      />

      <wui-input
        v-model="form.bDayUi"
        type="date"
        :min="BIRTH_DATE_MIN"
        :max="birthDateMax"
        :placeholder="t('common.dateOfBirth')"
        :error="!bDayIsValid"
        @input="onBirthDateInput"
        @blur="validateBirthDate"
      />
    </div>

    <template #footer>
      <wui-btn
        :size="40"
        auto-loading
        variant="brand"
        state="filled"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ isEditContact ? $t('common.save') : $t('common.add') }}
      </wui-btn>
    </template>
  </wui-modal>
</template>

<script lang="ts" setup>
import { WuiBtn, WuiInput, WuiModal, useRenderNotifier, closeDialogKey, safeInject } from '@wui/common-library'
import dayjs from 'dayjs'
import { reactive, computed, ref, watch } from 'vue'


import { useStoreContacts } from '@/entities/contact'
import { useStoreGroups } from '@/entities/contact-group'

import { UserAvatarInput } from '@/shared/components/avatar-input'
import { useApi } from '@/shared/composables/api'
import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'
import { DATE_FORMAT } from '@/shared/utils/constants'
import { getUserAvatarUrl } from '@/shared/utils/user-avatar'
import { isEmailValid } from '@/shared/utils/validators'

const closeModal = safeInject(closeDialogKey)
const { t } = useLocalization()
const { addUserContact, uploadUserContactPhoto, removeUserContactPhoto, editUserContact, fetchContactsByNumber } = useStoreContacts()
const { notifyError } = useRenderNotifier()
const props = defineProps<{
  contact?: Contact
  cloneFrom?: Contact
}>()

const formSource = computed(() => props.cloneFrom ?? props.contact)
const form = reactive({
  name: formSource.value?.name || '',
  groupId: props.contact?.groupIds?.[0] || '',
  email: formSource.value?.email || '',
  mobilePhone: formSource.value?.mobilePhone || '',
  organization: formSource.value?.organization || '',
  organizationalUnit: formSource.value?.organizationalUnit || '',
  position: formSource.value?.position || '',
  bDay: formSource.value?.bDay ? dayjs(formSource.value.bDay).toDate() : '',
  bDayUi: formSource.value?.bDay ? dayjs(formSource.value.bDay).format('YYYY-MM-DD') : '',
})

const groupsStore = useStoreGroups()

const avatar = ref<File | null>(null)
const isOldAvatarRemoved = ref(false)
const isDuplicatedMobilePhone = ref(false)
const duplicatedContactMobilePhoneName = ref('')
const isEditContact = computed(() => !!props.contact && !props.cloneFrom)

const addOrEditContactText = computed(() => {
  if (props.cloneFrom) {
    return t('mainAside.addToPersonalContacts')
  }
  if (isEditContact.value) {
    return t('mainAside.editingContact')
  }
  return t('mainAside.addingContact')
})

const BIRTH_DATE_MIN = '1901-01-01'
const birthDateMax = computed(() => dayjs().format('YYYY-MM-DD'))

const nameIsValid = ref(true)
const mobilePhoneIsValid = ref(true)
const emailIsValid = ref(true)
const bDayIsValid = ref(true)

const onBirthDateInput = (event: Event) => {
  bDayIsValid.value = true
  const input = event.target as HTMLInputElement
  const value = input.value
  if (!value) {
    return
  }

  const [yearStr, ...rest] = value.split('-')
  if (!yearStr || yearStr.length <= 4) {
    return
  }

  const sanitized = [yearStr.slice(0, 4), ...rest].join('-')
  form.bDayUi = sanitized
  input.value = sanitized
}

const validateEmail = (): boolean => {
  if (!form.email) {
    emailIsValid.value = true
    return true
  }
  const result = isEmailValid(form.email)
  emailIsValid.value = result
  return result
}

const isBirthDateUiValid = (bDayUi: string): boolean => {
  if (!bDayUi) {
    return true
  }

  const [yearStr, monthStr, dayStr] = bDayUi.split('-')
  if (!monthStr || !dayStr) {
    return false
  }

  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)

  if (year <= 1900 || !month || month < 1 || month > 12 || !day || day < 1 || day > 31) {
    return false
  }

  const parsedDate = dayjs(bDayUi, 'YYYY-MM-DD', true)
  if (!parsedDate.isValid()) {
    return false
  }

  const minDate = dayjs(BIRTH_DATE_MIN, 'YYYY-MM-DD', true)
  const today = dayjs().startOf('day')

  return !parsedDate.isBefore(minDate, 'day') && !parsedDate.isAfter(today, 'day')
}

const validateBirthDate = (): boolean => {
  if (!form.bDayUi) {
    bDayIsValid.value = true
    form.bDay = ''
    return true
  }

  const result = isBirthDateUiValid(form.bDayUi)
  bDayIsValid.value = result
  if (result) {
    form.bDay = dayjs(form.bDayUi, 'YYYY-MM-DD', true).toDate()
  }
  return result
}

const replaceNotNumbers = (event: Event) => {
  const input = event.target as HTMLInputElement
  const sanitized = input.value.replace(/[^0-9]/g, '').slice(0, 15)
  input.value = sanitized
  form.mobilePhone = sanitized
}

const validateForm = () => {
  nameIsValid.value = !!form.name
  mobilePhoneIsValid.value = !!form.mobilePhone
  const emailValid = validateEmail()
  const bdayValid = validateBirthDate()
  return nameIsValid.value && mobilePhoneIsValid.value && emailValid && bdayValid
}

const fetchDuplicatedContacts = async () => {
  const contacts = await fetchContactsByNumber(form.mobilePhone)
  if (contacts && contacts.length > 0) {
    const filteredContacts = isEditContact.value
      ? contacts.filter(contact => contact.guid !== props.contact?.guid)
      : contacts

    if (filteredContacts.length > 0) {
      isDuplicatedMobilePhone.value = true
      duplicatedContactMobilePhoneName.value = filteredContacts[0]?.name ?? ''
    } else {
      isDuplicatedMobilePhone.value = false
      duplicatedContactMobilePhoneName.value = ''
    }
  } else {
    isDuplicatedMobilePhone.value = false
    duplicatedContactMobilePhoneName.value = ''
  }
}

const checkDuplicates = async (): Promise<boolean> => {
  await fetchDuplicatedContacts()
  if (isDuplicatedMobilePhone.value) {
    return false
  }
  return true
}

const submit = async () => {
  if (!validateForm()) {
    return
  }
  if (!(await checkDuplicates())) {
    return
  }
  let contact: Contact | undefined = undefined
  try {
    const bDayString = form.bDay ? dayjs(form.bDay as Date).format(DATE_FORMAT) : ''
    if (isEditContact.value) {
      const payload: Contact = {
        ...props.contact as Contact,
        name: form.name,
        email: form.email,
        mobilePhone: form.mobilePhone,
        organization: form.organization,
        organizationalUnit: form.organizationalUnit,
        position: form.position,
        groupIds: props.contact?.groupIds || (form.groupId ? [form.groupId] : []),
        bDay: bDayString,
      }
      contact = await editUserContact(payload)
    } else {
      const payload = {
        groupId: form.groupId,
        name: form.name,
        mobilePhone: form.mobilePhone,
        email: form.email,
        organization: form.organization,
        organizationalUnit: form.organizationalUnit,
        position: form.position,
        bDay: bDayString,
      }
      contact = await addUserContact(payload)
    }
    if (avatar.value && contact?.guid) {
      await uploadUserContactPhoto(contact.guid, avatar.value)
    } else if (contact?.guid && props.contact?.guid && props.contact?.isPhotoAvailable && !avatar.value && isOldAvatarRemoved.value) {
      await removeUserContactPhoto(contact.guid)
    }

    closeModal(contact)
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? isEditContact.value ? `${t('mainAside.editContactError')}: ${e.message}`:  `${t('mainAside.addContactError')} ${e.message}`: t('common.unknownError')
    notifyError(errorMessage)
  }
}

const canSubmit = computed(() => {
  const nameValid = !!form.name
  const mobilePhoneValid = !!form.mobilePhone
  const emailValid = !form.email || isEmailValid(form.email)
  const bDayValid = isBirthDateUiValid(form.bDayUi)

  return nameValid && mobilePhoneValid && emailValid && bDayValid
})

watch(() => form.name, (newValue) => {
  nameIsValid.value = !!newValue
})

watch(() => form.mobilePhone, (newValue) => {
  mobilePhoneIsValid.value = !!newValue
  if (isDuplicatedMobilePhone.value) {
    isDuplicatedMobilePhone.value = false
  }
})

watch(() => form.email, (newValue) => {
  if (newValue) {
    emailIsValid.value = isEmailValid(newValue)
  } else {
    emailIsValid.value = true
  }
})

watch(() => form.bDayUi, (newValue) => {
  bDayIsValid.value = isBirthDateUiValid(newValue)
})

watch(() => groupsStore.groups, async () => {
  if (!groupsStore.userGroupsArr.length) {
    try {
      const response = await useApi().addUserGroup(t('common.userGroup'))
      form.groupId = response.data.guid
    } finally {
      groupsStore.load()
    }
  } else {
    form.groupId = groupsStore.userGroupsArr[0]?.guid ?? ''
  }
}, { immediate: true })
</script>

<style scoped>
.modal-scroll {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
